import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { COMPANY } from '../content/seo';
import { trackEvent } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestQuoteDialog({ open, onOpenChange, defaultInterest = '' }) {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    industry: '',
    interest: defaultInterest,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) trackEvent('request_quote_open', { interest: defaultInterest });
  }, [open, defaultInterest]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/landing/messages`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message:
          `[QUOTE REQUEST]\nCompany: ${form.company}\nIndustry: ${form.industry}\n` +
          `Interested in: ${form.interest}\n\n${form.message}`,
      });
      trackEvent('quote_request_submitted', { industry: form.industry, interest: form.interest });
      toast.success('Thanks — our sales team will get back to you within one business day.');
      onOpenChange(false);
      setForm({ name: '', company: '', email: '', phone: '', industry: '', interest: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>Request a Quote</DialogTitle>
          <DialogDescription>
            Tell us a bit about your business — we'll respond within one business day with
            pricing and delivery windows.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Your name *"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="quote-name"
            />
            <Input
              placeholder="Company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="quote-company"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="email"
              placeholder="Email *"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="quote-email"
            />
            <Input
              placeholder="Phone *"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="quote-phone"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Industry (e.g. Hotel)"
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              data-testid="quote-industry"
            />
            <Input
              placeholder="What you need"
              value={form.interest}
              onChange={(e) => setForm({ ...form, interest: e.target.value })}
              data-testid="quote-interest"
            />
          </div>
          <Textarea
            placeholder="Volume, frequency, delivery location…"
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="quote-message"
          />
          <Button type="submit" disabled={submitting} className="w-full" data-testid="quote-submit">
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Prefer to call? {COMPANY.phoneDisplay} · {COMPANY.email}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
